import { useState } from 'react';
import { Search, ShieldCheck, ShieldOff, MonitorSmartphone, Clock, AlertTriangle, CheckCircle2, Info, Loader2, User, KeyRound } from 'lucide-react';
import { adminApi } from '@/lib/api';

interface DebugResult {
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    is_active: boolean;
    type: 'platform' | 'tenant';
    schema: string | null;
    totp_enabled: boolean;
    recovery_codes_remaining: number;
    last_login_at: string | null;
  };
  sessions: {
    count: number;
    active: Array<{ sid: string; expiresAt: string; ip?: string; ua?: string; created_at?: string; last_active?: string }>;
  };
  authLog: Array<{ type: string; result: 'ok' | 'fail' | 'info'; ip: string; ts: string; detail?: string; role?: string }>;
  totpLockout: { locked: boolean; attempts: number; ttlSec: number | null };
}

export default function AuthDebugPage() {
  const [email, setEmail]             = useState('');
  const [tenantSlug, setTenantSlug]   = useState('');
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [result, setResult]           = useState<DebugResult | null>(null);

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const { data } = await adminApi.debugAuth(email.trim(), tenantSlug.trim() || undefined);
      setResult(data);
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Search failed');
    } finally {
      setLoading(false);
    }
  }

  function resultBadge(res: 'ok' | 'fail' | 'info') {
    if (res === 'ok')   return <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full"><CheckCircle2 size={11}/>ok</span>;
    if (res === 'fail') return <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full"><AlertTriangle size={11}/>fail</span>;
    return <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full"><Info size={11}/>info</span>;
  }

  function fmtTs(ts: string) {
    try { return new Date(ts).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'medium' }); }
    catch { return ts; }
  }

  function parseUA(ua: string = '') {
    if (!ua) return 'Unknown browser';
    if (/chrome/i.test(ua) && !/edge/i.test(ua)) return 'Chrome';
    if (/firefox/i.test(ua)) return 'Firefox';
    if (/safari/i.test(ua) && !/chrome/i.test(ua)) return 'Safari';
    if (/edge/i.test(ua)) return 'Edge';
    return ua.slice(0, 40);
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-navy">Auth Debugger</h1>
        <p className="text-sm text-gray-400 mt-0.5">
          Look up a user's login state, active sessions, and recent auth events for support troubleshooting.
        </p>
      </div>

      {/* Search form */}
      <form onSubmit={handleSearch} className="bg-white rounded-xl border border-gray-200 p-5 mb-6 flex gap-3 items-end">
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-500 mb-1">User email</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="doctor@hospital.com"
            required
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm
                       focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange"
          />
        </div>
        <div className="w-52">
          <label className="block text-xs font-medium text-gray-500 mb-1">Tenant schema <span className="text-gray-300">(optional)</span></label>
          <input
            type="text"
            value={tenantSlug}
            onChange={e => setTenantSlug(e.target.value)}
            placeholder="demo-hospital"
            className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm
                       focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange"
          />
        </div>
        <button
          type="submit"
          disabled={loading || !email.trim()}
          className="px-4 py-2 bg-orange text-navy text-sm font-semibold rounded-lg
                     hover:bg-orange-dark transition-colors disabled:opacity-40
                     flex items-center gap-2 whitespace-nowrap">
          {loading ? <Loader2 size={14} className="animate-spin"/> : <Search size={14}/>}
          {loading ? 'Searching…' : 'Look up'}
        </button>
      </form>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-6">
          {error}
        </div>
      )}

      {result && (
        <div className="space-y-5">

          {/* User card */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-navy/10 flex items-center justify-center">
                  <User size={18} className="text-navy"/>
                </div>
                <div>
                  <p className="font-semibold text-navy">{result.user.name}</p>
                  <p className="text-sm text-gray-400">{result.user.email}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                  result.user.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                  {result.user.is_active ? 'Active' : 'Suspended'}
                </span>
                <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 capitalize">
                  {result.user.role.replace(/_/g, ' ')}
                </span>
                <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">
                  {result.user.type === 'platform' ? 'Platform user' : result.user.schema}
                </span>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCell
                label="MFA"
                value={result.user.totp_enabled ? 'Enrolled' : 'Not set up'}
                icon={result.user.totp_enabled ? <ShieldCheck size={15} className="text-emerald-600"/> : <ShieldOff size={15} className="text-gray-400"/>}
                highlight={result.user.totp_enabled ? 'green' : 'gray'}
              />
              <StatCell
                label="Recovery codes"
                value={`${result.user.recovery_codes_remaining} remaining`}
                icon={<KeyRound size={15} className="text-orange"/>}
                highlight={result.user.recovery_codes_remaining > 0 ? 'orange' : 'red'}
              />
              <StatCell
                label="Active sessions"
                value={`${result.sessions.count} / 3`}
                icon={<MonitorSmartphone size={15} className="text-blue-500"/>}
                highlight="blue"
              />
              <StatCell
                label="Last login"
                value={result.user.last_login_at ? fmtTs(result.user.last_login_at) : 'Never'}
                icon={<Clock size={15} className="text-gray-400"/>}
                highlight="gray"
              />
            </div>
          </div>

          {/* TOTP lockout */}
          {(result.totpLockout.locked || result.totpLockout.attempts > 0) && (
            <div className={`rounded-xl border px-5 py-4 flex items-center gap-3 ${
              result.totpLockout.locked
                ? 'bg-red-50 border-red-200 text-red-700'
                : 'bg-amber-50 border-amber-200 text-amber-700'
            }`}>
              <AlertTriangle size={18}/>
              <div>
                <p className="text-sm font-semibold">
                  {result.totpLockout.locked ? 'TOTP locked out' : 'TOTP attempts recorded'}
                </p>
                <p className="text-xs mt-0.5">
                  {result.totpLockout.attempts} failed attempt{result.totpLockout.attempts !== 1 ? 's' : ''}.
                  {result.totpLockout.ttlSec !== null
                    ? ` Resets in ${Math.ceil(result.totpLockout.ttlSec / 60)} min.`
                    : ''}
                </p>
              </div>
            </div>
          )}

          {/* Active sessions */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
              <MonitorSmartphone size={15} className="text-gray-400"/>
              <span className="text-sm font-semibold text-navy">Active Sessions</span>
              <span className="ml-auto text-xs text-gray-400">{result.sessions.count} active</span>
            </div>
            {result.sessions.active.length === 0 ? (
              <p className="text-sm text-gray-400 px-5 py-4">No active sessions</p>
            ) : (
              <div className="divide-y divide-gray-50">
                {result.sessions.active.map(sess => (
                  <div key={sess.sid} className="px-5 py-3 flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-navy">{parseUA(sess.ua)}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {sess.ip ?? 'IP unknown'}
                        {sess.created_at ? ` · started ${fmtTs(sess.created_at)}` : ''}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-gray-400">Expires</p>
                      <p className="text-xs text-navy font-medium">{fmtTs(sess.expiresAt)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Auth event log */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
              <Clock size={15} className="text-gray-400"/>
              <span className="text-sm font-semibold text-navy">Auth Event Log</span>
              <span className="ml-auto text-xs text-gray-400">last {result.authLog.length} events</span>
            </div>
            {result.authLog.length === 0 ? (
              <p className="text-sm text-gray-400 px-5 py-4">No events recorded yet</p>
            ) : (
              <div className="divide-y divide-gray-50">
                {result.authLog.map((ev, i) => (
                  <div key={i} className="px-5 py-3 flex items-start gap-3">
                    <div className="mt-0.5">{resultBadge(ev.result)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-navy font-medium">{ev.type.replace(/_/g, ' ')}</p>
                      {ev.detail && <p className="text-xs text-gray-400 mt-0.5">{ev.detail}</p>}
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-xs text-gray-400 font-mono">{ev.ip}</p>
                      <p className="text-xs text-gray-300 mt-0.5">{fmtTs(ev.ts)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
}

function StatCell({ label, value, icon, highlight }: {
  label: string; value: string; icon: React.ReactNode;
  highlight: 'green' | 'orange' | 'blue' | 'red' | 'gray';
}) {
  const bg: Record<string, string> = {
    green: 'bg-emerald-50', orange: 'bg-orange/10', blue: 'bg-blue-50',
    red: 'bg-red-50', gray: 'bg-gray-50',
  };
  return (
    <div className={`${bg[highlight]} rounded-lg px-3 py-2.5`}>
      <div className="flex items-center gap-1.5 mb-1">{icon}<span className="text-xs text-gray-400">{label}</span></div>
      <p className="text-sm font-semibold text-navy">{value}</p>
    </div>
  );
}
